import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { formatQuantity } from "@/lib/units";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Send, Package, Recycle, CheckCircle2, XCircle } from "lucide-react";
import { celebrate } from "@/lib/celebrate";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function ReuseConversation() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [completion, setCompletion] = useState(null); // { kind, message }
  const [completing, setCompleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const endRef = useRef(null);

  const loadMessages = () => api.get(`/reuse-requests/${id}/messages`).then(({ data }) => setMessages(data));

  useEffect(() => {
    api.get(`/reuse-requests/${id}`)
      .then(({ data }) => { setCtx(data); return loadMessages(); })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "No se pudo cargar la conversación"));
  }, [id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Sprint 11.1: polling automático de mensajes + estado cada 15s mientras el hilo
  // esté abierto y la pestaña esté visible. Detecta cambios de estado (completado/
  // cancelado por la otra parte) y actualiza sin recarga manual.
  useEffect(() => {
    if (!ctx || ctx.status !== "pending") return;
    let stopped = false;
    const tick = async () => {
      if (stopped || document.hidden) return;
      try {
        const [ctxRes, msgsRes] = await Promise.all([
          api.get(`/reuse-requests/${id}`),
          api.get(`/reuse-requests/${id}/messages`),
        ]);
        if (stopped) return;
        setCtx((prev) => (prev && prev.status !== ctxRes.data.status ? ctxRes.data : ctxRes.data));
        setMessages(msgsRes.data);
      } catch { /* silent; el próximo tick reintenta */ }
    };
    const iv = setInterval(tick, 15000);
    // Refresh inmediato al volver a enfocar la pestaña.
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { stopped = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [ctx?.status, id]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const { data } = await api.post(`/reuse-requests/${id}/messages`, { body: text });
      setMessages((m) => [...m, data]);
      setBody("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falló");
    } finally {
      setSending(false);
    }
  };

  const complete = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post(`/reuse-requests/${id}/complete`);
      setCtx((c) => ({ ...c, status: "completed" }));
      if (data.points_awarded > 0) {
        celebrate("Reutilización completada", { description: `+${data.points_awarded} puntos para tu proyecto.` });
      }
      // Sprint 12: mostramos SIEMPRE la ventana de cierre (aunque ya estuviera completada),
      // con contenido distinto para sobrante vs. equipamiento.
      setCompletion({ kind: data.completion_kind, message: data.completion_message });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falló");
    } finally {
      setCompleting(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("¿Cancelar esta solicitud? Se borrarán los mensajes de la conversación y el activo regresará a estar disponible.")) return;
    setCancelling(true);
    try {
      await api.post(`/reuse-requests/${id}/cancel`);
      toast.success("Solicitud cancelada. El activo vuelve a estar disponible.");
      if (ctx?.resource_id) nav(`/assets/${ctx.resource_id}`);
      else nav("/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cancelar la solicitud");
    } finally {
      setCancelling(false);
    }
  };

  const isOpen = ctx?.status !== "completed" && ctx?.status !== "cancelled";

  if (error) {
    return (
      <div className="max-w-2xl" data-testid="conversation-error">
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-10 text-center text-sm text-[#5C6A7D]">{error}</div>
      </div>
    );
  }
  if (!ctx) return <div className="text-sm text-[#5C6A7D]">Cargando…</div>;

  return (
    <div className="max-w-3xl space-y-5" data-testid="conversation-page">
      <Link to="/" className="inline-flex items-center text-xs uppercase tracking-[0.18em] text-[#5C6A7D] hover:text-[#0A2540]">
        <ArrowLeft size={14} className="mr-1" /> Volver
      </Link>

      {/* Resource + project context */}
      <div className="bg-[#0A2540] text-white rounded-sm p-5" data-testid="conversation-context">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/60">
          <Recycle size={13} /> Solicitud de reutilización
        </div>
        <Link to={`/assets/${ctx.resource_id}`} className="font-display text-2xl font-extrabold mt-1 inline-flex items-center gap-2 hover:underline">
          <Package size={20} /> {ctx.resource_name}
        </Link>
        <div className="mt-3 grid sm:grid-cols-3 gap-3 text-xs">
          <div><div className="text-white/50 uppercase tracking-[0.15em] text-[10px]">Cantidad</div><div className="font-semibold mt-0.5">{formatQuantity(ctx.quantity, ctx.unit)}</div></div>
          <div><div className="text-white/50 uppercase tracking-[0.15em] text-[10px]">Proyecto origen</div><div className="font-semibold mt-0.5">{ctx.source_project_name || "—"}</div></div>
          <div><div className="text-white/50 uppercase tracking-[0.15em] text-[10px]">Proyecto solicitante</div><div className="font-semibold mt-0.5">{ctx.requester_project_name || "—"}</div></div>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          {ctx.status === "completed" ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 text-white text-xs font-semibold" data-testid="reuse-completed-badge">
              <CheckCircle2 size={14} /> Reutilización completada
            </span>
          ) : ctx.status === "cancelled" ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 text-white text-xs font-semibold" data-testid="reuse-cancelled-badge">
              <XCircle size={14} /> Solicitud cancelada
            </span>
          ) : (
            <>
              <Button onClick={complete} disabled={completing} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-sm h-10" data-testid="complete-reuse-btn">
                <CheckCircle2 size={16} className="mr-1.5" /> Completar reutilización (+100)
              </Button>
              <Button onClick={cancel} disabled={cancelling} variant="outline" className="rounded-sm h-10 bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white" data-testid="cancel-reuse-btn">
                <XCircle size={16} className="mr-1.5" /> Cancelar solicitud
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Thread */}
      <div className="bg-white border border-[#E2E8F0] rounded-sm p-4 space-y-3 min-h-[240px]" data-testid="conversation-thread">
        {messages.length === 0 && <div className="text-sm text-[#5C6A7D] text-center py-8">Aún no hay mensajes.</div>}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.is_me ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.is_me ? "bg-[#10B981] text-white" : "bg-[#F1F5F9] text-[#0A2540]"}`}>
              <div className={`text-[11px] font-semibold ${m.is_me ? "text-white/80" : "text-[#5C6A7D]"}`}>{m.sender_name}</div>
              <div className="text-sm mt-0.5 whitespace-pre-wrap break-words">{m.body}</div>
              <div className={`text-[10px] mt-1 ${m.is_me ? "text-white/60" : "text-[#94A3B8]"}`}>{fmtTime(m.created_at)}</div>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {/* Reply — bloqueado si la solicitud ya está cerrada. */}
      {isOpen ? (
        <div className="flex items-end gap-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
            placeholder="Escribe un mensaje…"
            className="rounded-sm min-h-[52px]"
            data-testid="reply-input"
          />
          <Button onClick={send} disabled={sending} className="bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm h-[52px] px-5" data-testid="reply-send">
            <Send size={16} />
          </Button>
        </div>
      ) : (
        <div className="text-xs text-[#5C6A7D] bg-[#FAFAFA] border border-[#E2E8F0] rounded-sm p-3 text-center" data-testid="conversation-closed-notice">
          Esta conversación está cerrada. El historial permanece disponible como referencia.
        </div>
      )}

      {/* Sprint 12: ventana de cierre al completar la reutilización.
          Contenido distinto para sobrante (menciona pago/Compras) vs. equipamiento (leyenda breve). */}
      <Dialog open={!!completion} onOpenChange={(o) => !o && setCompletion(null)}>
        <DialogContent className="rounded-sm" data-testid="completion-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {completion?.kind === "surplus" ? "Reutilización de sobrante completada" : "Reutilización completada"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#0A2540] leading-relaxed py-2" data-testid={`completion-message-${completion?.kind || 'unknown'}`}>
            {completion?.message}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setCompletion(null)}
              className="rounded-sm bg-[#0A2540] hover:bg-[#173B63] text-white"
              data-testid="completion-close-btn"
            >
              Cerrar ventana
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
